// 🔒 PROTECTED — PricePAL Compare
import React, { useRef } from 'react';
import { trackRegisterStarted } from '../../utils/tracking';

/**
 * AuthScreen — Login / Signup form (extracted from UserApp.js).
 *
 * iter158 additions (2026-07-07):
 *   • `forceSignupMode` prop  — when true (arrived via /register), hides
 *     the in-form login/signup toggle and replaces it with a link that
 *     redirects existing users to /app for login.
 *   • First-focus tracker fires `register_started` (top-of-funnel event
 *     that lets us distinguish "landed on /register" from "started to fill
 *     the form"). Fires only once per session.
 */
const AuthScreen = ({
  isSignup, setIsSignup,
  email, setEmail,
  password, setPassword,
  name, setName,
  showUserPassword, setShowUserPassword,
  marketingConsent, setMarketingConsent,
  referralCode, setReferralCode,
  rememberMe, setRememberMe,
  isBetaSignup,
  forceSignupMode = false,
  authError, setAuthError,
  loading,
  handleAuth,
}) => {
  const registerStartedFiredRef = useRef(false);

  const handleFirstFocus = () => {
    if (!isSignup) return;                         // login form → not a registration attempt
    if (registerStartedFiredRef.current) return;   // fire once per session
    registerStartedFiredRef.current = true;
    trackRegisterStarted();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md" data-testid="auth-container">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-green-600 mb-2">PricePAL Compare</h1>
          <p className="text-gray-600">
            {isSignup 
              ? "Join the UK's smartest price comparison app — start saving today!" 
              : "Welcome back! Login to continue saving."}
          </p>
          {isSignup && (
            <div className="mt-3 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3">
              <p className="text-sm text-green-700 font-medium">7-day free Premium trial · Card required · No charge today · Cancel any time.</p>
            </div>
          )}
        </div>

        <form onSubmit={handleAuth} data-testid="auth-form" className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} onFocus={handleFirstFocus}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              placeholder="your@email.com" required data-testid="auth-email-input" />
          </div>

          {isSignup && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} onFocus={handleFirstFocus}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Your name" required data-testid="auth-name-input" />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">Password</label>
              {!isSignup && (
                <a href="/reset-password" className="text-xs text-green-600 hover:text-green-700 font-medium">Forgot password?</a>
              )}
            </div>
            <div className="relative">
              <input type={showUserPassword ? 'text' : 'password'} value={password}
                onChange={(e) => setPassword(e.target.value)} onFocus={handleFirstFocus}
                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="••••••••" required data-testid="auth-password-input" />
              <button type="button" onClick={() => setShowUserPassword(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1} aria-label={showUserPassword ? 'Hide password' : 'Show password'}>
                {showUserPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                )}
              </button>
            </div>
          </div>

          {isSignup && (
            <>
              <label className="flex items-start space-x-3 cursor-pointer">
                <input type="checkbox" checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-1 w-4 h-4 text-green-600 focus:ring-green-500"
                  data-testid="auth-marketing-consent-checkbox" />
                <span className="text-xs text-gray-600">I&apos;d like to receive money-saving tips and updates from PricePAL Compare. Unsubscribe anytime.</span>
              </label>
              {/* iter172i (2026-08-22): PECR Reg 22(3) soft opt-in notice. Even if
                  the box above is left unticked, once you become a customer we may
                  email you about similar products/services. UK ICO guidance says
                  this notice MUST be given at the point of data collection and MUST
                  be paired with an easy opt-out (every marketing email has one). */}
              <p className="text-[11px] text-gray-500 pl-7 -mt-2 leading-relaxed" data-testid="auth-soft-optin-notice">
                By creating an account, you accept that we may occasionally email you about our own similar products and services (UK PECR &quot;soft opt-in&quot;). You can opt out from any email or in Settings at any time.
              </p>
            </>
          )}

          {isSignup && (
            <details className="text-sm">
              <summary className="cursor-pointer text-green-600 hover:text-green-700 font-medium">Have a referral code?</summary>
              <input type="text" value={referralCode || (isBetaSignup ? 'BETA001' : '')}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="w-full mt-2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                placeholder="Enter referral code" maxLength={20} data-testid="auth-referral-input" />
            </details>
          )}

          <label className="flex items-center space-x-3 cursor-pointer">
            <input type="checkbox" checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="w-4 h-4 text-green-600 focus:ring-green-500 rounded" />
            <span className="text-sm text-gray-700">Remember me</span>
          </label>

          {authError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm" data-testid="auth-error">{authError}</div>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition disabled:opacity-50"
            data-testid="auth-submit-btn">
            {loading ? 'Processing...' : isSignup ? 'Create Account' : 'Login'}
          </button>
        </form>

        <div className="mt-6 text-center">
          {forceSignupMode ? (
            // iter158 — /register route hides the toggle so the marketing funnel
            // stays clean. Existing users are sent straight to /app for login.
            <a
              href="/app"
              className="text-green-600 hover:text-green-700 font-medium text-sm"
              data-testid="auth-goto-login-link"
            >
              Already have an account? Log in
            </a>
          ) : (
            <button onClick={() => { setIsSignup(!isSignup); setAuthError(''); }}
              className="text-green-600 hover:text-green-700 font-medium text-sm" data-testid="auth-toggle-btn">
              {isSignup ? 'Already have an account? Login' : "Don\u2019t have an account? Sign Up"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthScreen;
